import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, MessageCircle, Sparkles } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/config/routes';
import { pricingPackages } from '@/config/pricingPackages';

const pricingContent = {
  nl: {
    eyebrow: 'Prijzen',
    title: 'Duidelijke websitepakketten voor zelfstandigen & KMO\'s',
    subtitle: 'Bij Netblitz krijg je een professionele website zonder gedoe, tegen een eerlijk tarief. Geen verborgen kosten: je kiest het pakket dat bij jouw bedrijf past en weet op voorhand waar je aan toe bent.',
    subtitle2: 'Heb je vragen of wil je snel weten welk pakket het best bij jouw bedrijf past? Dan kun je ook rechtstreeks via onze chatbot een prijsaanvraag of offerteaanvraag indienen.',
    mostChosen: 'Meest gekozen',
    oneTimeLabel: 'Eenmalige prijs',
    monthlyLabel: 'Maandelijkse kosten',
    monthlyOptional: '(optioneel)',
    exclVat: 'excl. btw',
    packages: [
      {
        name: 'Starter Website',
        subtitle: 'Perfect om als zelfstandige of kleine dienstverlener snel professioneel online te staan.',
        price: 'vanaf €599',
        monthlyPrice: '€29/maand',
        monthlyNote: 'Hosting, domein en basis ondersteuning inbegrepen.',
        popular: false,
        features: [
          '1 tot 4 secties op één pagina',
          'Professioneel en mobielvriendelijk ontwerp',
          'Basis-SEO inbegrepen',
          'Contactformulier of WhatsApp-knop',
          '1 revisieronde'
        ]
      },
      {
        name: 'Business Website',
        subtitle: 'Voor KMO\'s en dienstverleners die meer willen dan een online visitekaartje.',
        price: '€899 – €1.099',
        monthlyPrice: '€39/maand',
        monthlyNote: 'Hosting, domein, updates en basis ondersteuning inbegrepen.',
        popular: true,
        features: [
          '4 tot 8 pagina\'s',
          'Sterk ontwerp op maat van jouw sector',
          'Uitgebreidere SEO-structuur',
          'Meertalige opbouw mogelijk',
          '2 revisierondes'
        ]
      },
      {
        name: 'Website + Klantzone',
        subtitle: 'Voor bedrijven die naast een website ook een digitale klantzone of portaal willen.',
        price: 'vanaf €1.450',
        monthlyPrice: 'vanaf €49/maand',
        monthlyNote: 'Hosting website, portaal, updates en support inbegrepen.',
        popular: false,
        features: [
          'Alles uit het Business pakket',
          'Beveiligde klantzone via login',
          'Documenten, offertes of status online raadpleegbaar',
          'Toegang voor beheerder en klanten',
          '2 tot 3 revisierondes'
        ]
      },
      {
        name: 'Website + Planning & Uren',
        subtitle: 'Voor schoonmaakbedrijven en dienstverleners met teams op de baan.',
        price: 'vanaf €1.950',
        monthlyPrice: 'vanaf €79/maand',
        monthlyNote: 'Hosting website, app, updates, bugfixes en support inbegrepen.',
        popular: false,
        features: [
          'Website + digitale planningstool',
          'Werkbonnen met foto\'s en opmerkingen',
          'Urenregistratie per medewerker',
          'Dashboard met overzicht',
          'Mobielvriendelijk voor teams op de baan'
        ]
      }
    ]
  },
  en: {
    eyebrow: 'Pricing',
    title: 'Clear website packages for freelancers & SMEs',
    subtitle: 'At Netblitz you get a professional website without hassle, at a fair price. No hidden costs: you choose the package that fits your business.',
    subtitle2: 'Have questions? You can also submit a quote request directly via our chatbot.',
    mostChosen: 'Most popular',
    oneTimeLabel: 'One-time price',
    monthlyLabel: 'Monthly costs',
    monthlyOptional: '(optional)',
    exclVat: 'excl. VAT',
    packages: [
      {
        name: 'Starter Website',
        subtitle: 'Perfect for freelancers to quickly get online professionally.',
        price: 'from €599',
        monthlyPrice: '€29/month',
        monthlyNote: 'Hosting, domain and basic support included.',
        popular: false,
        features: ['1 to 4 sections on one page', 'Professional mobile-friendly design', 'Basic SEO included', 'Contact form or WhatsApp button', '1 revision round']
      },
      {
        name: 'Business Website',
        subtitle: 'For SMEs that want more than an online business card.',
        price: '€899 – €1,099',
        monthlyPrice: '€39/month',
        monthlyNote: 'Hosting, domain, updates and support included.',
        popular: true,
        features: ['4 to 8 pages', 'Strong design tailored to your sector', 'Extended SEO structure', 'Multilingual possible', '2 revision rounds']
      },
      {
        name: 'Website + Client Portal',
        subtitle: 'For companies that also want a digital client zone.',
        price: 'from €1,450',
        monthlyPrice: 'from €49/month',
        monthlyNote: 'Website hosting, portal, updates and support included.',
        popular: false,
        features: ['Everything from Business package', 'Secured client zone via login', 'Documents, quotes online', 'Access for admin and clients', '2-3 revision rounds']
      },
      {
        name: 'Website + Planning & Hours',
        subtitle: 'For service companies with field teams.',
        price: 'from €1,950',
        monthlyPrice: 'from €79/month',
        monthlyNote: 'Website hosting, app, updates and support included.',
        popular: false,
        features: ['Website + digital planning tool', 'Work orders with photos', 'Time registration per employee', 'Dashboard with overview', 'Mobile-friendly for teams']
      }
    ]
  }
};

export const PricingPreview = () => {
  const { language } = useLanguage();
  const content = pricingContent[language] || pricingContent.nl;
  const currentPackages = pricingPackages[language] || pricingPackages.nl;
  const pricingPath = getLocalizedPath('pricing', language);

  const monthlyLabels = {
    nl: { manage: 'Beheer & hosting', platform: 'Platform, hosting & support' },
    en: { manage: 'Management & hosting', platform: 'Platform, hosting & support' },
    fr: { manage: 'Gestion & hébergement', platform: 'Plateforme, hébergement & support' },
    de: { manage: 'Verwaltung & Hosting', platform: 'Plattform, Hosting & Support' },
  };
  const mLabel = monthlyLabels[language] || monthlyLabels.nl;

  const openChatbot = () => {
    window.dispatchEvent(new CustomEvent('netblitz:open-quote'));
  };

  return (
    <section 
      id="pricing"
      className="py-20 sm:py-28 bg-[#F9FAFB]"
      data-testid="pricing-preview-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4 bg-blue-100 text-[#2563EB]">
            {content.eyebrow}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-[#111827] mb-6 leading-tight">
            {content.title}
          </h2>
          <p className="text-lg text-[#4B5563] leading-relaxed mb-4">{content.subtitle}</p>
          <p className="text-base text-[#6B7280] leading-relaxed">{content.subtitle2}</p>
        </motion.div>

        {/* Pricing Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {currentPackages.map((pkg, index) => (
            <motion.article
              key={pkg.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative flex flex-col p-7 rounded-[18px] transition-all duration-300 hover:-translate-y-1 ${
                pkg.popular 
                  ? 'bg-white border-[1.5px] border-[#2563EB] shadow-[0_18px_45px_rgba(37,99,235,0.12)]' 
                  : 'bg-white border border-gray-200 shadow-[0_8px_24px_rgba(17,24,39,0.06)] hover:shadow-[0_18px_45px_rgba(17,24,39,0.10)]'
              }`}
            >
              {pkg.popular && (
                <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold text-white bg-[#F97316]">
                  {content.mostChosen}
                </div>
              )}

              <h3 className="text-2xl font-extrabold text-[#111827] mb-2">{pkg.name}</h3>
              <p className="text-[#6B7280] text-sm leading-relaxed mb-5">{pkg.subtitle}</p>

              {/* Price block */}
              <div className="p-4 rounded-xl mb-5 bg-[#F3F4F6] border border-gray-200">
                <p className="text-xs font-bold uppercase tracking-wider text-[#6B7280] mb-2">{content.oneTimeLabel}</p>
                <p className="text-3xl font-extrabold text-[#111827]">
                  {pkg.price} <span className="text-sm font-semibold text-[#6B7280]">{content.exclVat}</span>
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-5 flex-grow">
                {pkg.featuresShort.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold bg-blue-100 text-[#2563EB]">
                      ✓
                    </span>
                    <span className="text-[#4B5563] text-sm leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Monthly block */}
              <div className="p-4 rounded-xl bg-[#F3F4F6] border border-gray-200">
                <p className="text-xs font-bold uppercase tracking-wider text-[#6B7280] mb-2">
                  {(pkg.slug === 'portal' || pkg.slug === 'pro') ? mLabel.platform : mLabel.manage}
                </p>
                <p className="text-xl font-extrabold text-[#111827]">
                  {pkg.monthlyPrice} <span className="text-sm font-semibold text-[#6B7280]">{content.exclVat}</span>
                </p>
                <p className="text-sm text-[#6B7280] mt-2">{pkg.monthlyNote}</p>
              </div>

              <Link
                to={`${getLocalizedPath('contact', language)}?pakket=${encodeURIComponent(pkg.name)}`}
                className={`mt-5 inline-flex items-center justify-center gap-2 w-full min-h-[52px] px-5 py-3.5 rounded-full font-bold text-white transition-all hover:-translate-y-0.5 ${
                  pkg.popular
                    ? 'bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] shadow-lg shadow-blue-500/30'
                    : 'bg-[#111827] hover:bg-[#1F2937]'
                }`}
                data-testid={`pricing-preview-cta-${pkg.slug}`}
              >
                {pkg.cta}
                <ArrowRight size={18} />
              </Link>
            </motion.article>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <Link
              to={pricingPath}
              className="inline-flex items-center gap-2 text-[#2563EB] font-semibold hover:gap-3 transition-all"
              data-testid="pricing-preview-cta"
            >
              Bekijk alle details
              <ArrowRight size={20} />
            </Link>
            <span className="text-gray-400 hidden sm:inline">of</span>
            <button
              onClick={openChatbot}
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#EA580C] hover:to-[#C2410C] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              data-testid="pricing-chatbot-cta"
            >
              <Sparkles size={18} />
              Direct offerte via chatbot
              <MessageCircle size={18} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
